// src/stores/EventsStore.js - Zustand Store replacing EventsSocketService + EventsProvider
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';
import { formatEventsForCalendar } from '../utils/EventFormatter';

// Store creation with selector subscription middleware
const useEventsStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE (mirrors EventsProvider exactly)
    // ================================
    
    // Main events data (single source of truth)
    events: [],
    eventsLoading: false,
    eventsPagination: null,

    // Selected event state (for EventDetails page)
    selectedEvent: null,
    selectedEventLoading: false,
    
    // Calendar-specific state (derived from events, but cached for performance)
    calendarEvents: [],
    calendarLoading: false,
    
    // Connection state
    isConnected: false,
    
    // Cache state (from EventsSocketService)
    cache: {
      events: [],
      pagination: null,
      selectedEvent: null,
      calendarEvents: [],
      lastFetch: null
    },
    
    // Internal flags
    _initialized: false,
    
    // ================================
    // SOCKET OPERATIONS (from EventsSocketService)
    // ================================
    
    /**
     * Request events from server - NO RETURN VALUE
     * @param {object} options - Loading options
     */
    loadEvents: (options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ EventsStore: Socket service not initialized');
        return;
      }

      console.log('📅 EventsStore: Loading events with options:', options);

      // Determine if this is calendar mode or list mode
      const isCalendarMode = options.start_date && options.end_date;

      if (isCalendarMode) {
        set({ calendarLoading: true });
      } else {
        set({ eventsLoading: true });
      }

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_EVENTS, options);
    },
    
    /**
     * Request detailed information for a specific event
     * @param {string} eventId - Event ID
     */
    loadEventDetails: (eventId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ EventsStore: Socket service not initialized');
        return;
      }

      console.log('📋 EventsStore: Loading event details:', eventId);

      set({ selectedEventLoading: true });

      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_EVENT_DETAILS, {
        event_id: eventId
      });
    },
    
    /**
     * Request to create a new event
     * @param {object} eventData - Event information
     */
    createEvent: (eventData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ EventsStore: Socket service not initialized');
        return;
      }

      console.log('➕ EventsStore: Creating event:', eventData.title || eventData.event_category || 'New Event');

      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_EVENT, eventData);
    },
    
    /**
     * Request to update an existing event
     * @param {string} eventId - Event ID
     * @param {object} updateData - Fields to update
     */
    updateEvent: (eventId, updateData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ EventsStore: Socket service not initialized');
        return;
      }

      console.log('✏️ EventsStore: Updating event:', eventId, updateData);

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_EVENT, {
        event_id: eventId,
        ...updateData
      });
    },
    
    /**
     * Request to delete an event
     * @param {string} eventId - Event ID
     */
    deleteEvent: (eventId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ EventsStore: Socket service not initialized');
        return;
      }

      console.log('🗑️ EventsStore: Deleting event:', eventId);

      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_EVENT, {
        event_id: eventId
      });
    },

    /**
     * Request to update event status (convenience method)
     * @param {string} eventId - Event ID
     * @param {string} newStatus - New status
     * @param {object} statusData - Additional status data
     */
    updateEventStatus: (eventId, newStatus, statusData = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ EventsStore: Socket service not initialized');
        return;
      }

      console.log('🔄 EventsStore: Updating event status:', eventId, newStatus);

      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_EVENT_STATUS, {
        event_id: eventId,
        status: newStatus,
        ...statusData
      });
    },
    
    // ================================
    // UTILITY FUNCTIONS (from EventsProvider)
    // ================================

    clearSelectedEvent: () => {
      set({ selectedEvent: null });
    },
    
    refreshEvents: (options = {}) => {
      // Clear cache first
      set(state => ({
        cache: {
          ...state.cache,
          events: [],
          pagination: null,
          selectedEvent: null,
          calendarEvents: [],
          lastFetch: null
        }
      }));
      
      // Then reload with force option
      get().loadEvents({ ...options, force: true });
    },
    
    getEventById: (eventId) => {
      const { events } = get();
      return events.find(e => e.id === eventId) || null;
    },
    
    // Get events for specific customer
    getCustomerEvents: (customerId) => {
      const { events } = get();
      return events.filter(event => event.customer_id === customerId);
    },
    
    // Get events for date range (for calendar)
    getEventsForDateRange: (startDate, endDate) => {
      const { events } = get();
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      return events.filter(event => {
        const eventDate = new Date(event.event_date);
        return eventDate >= start && eventDate <= end;
      });
    },
    
    // ================================
    // COMPUTED VALUES (from EventsProvider)
    // ================================
    
    // Get appointments (events with service category) - computed in hook
    getAppointments: () => {
      const { events } = get();
      return events.filter(event => 
        event.event_category === 'service' || 
        event.event_category === 'appointment'
      );
    },
    
    // ================================
    // SMART CACHE MANAGEMENT (from EventsProvider)
    // ================================
    
    _addEventsToCache: (newEvents, source = 'unknown') => {
      if (!Array.isArray(newEvents) || newEvents.length === 0) return;
      
      console.log(`📅 EventsStore: Adding ${newEvents.length} events to cache from ${source}`);
      
      set(state => {
        // Get existing event IDs for deduplication
        const existingIds = new Set(state.events.map(e => e.id));
        
        // Filter out events that already exist
        const trulyNewEvents = newEvents.filter(event => !existingIds.has(event.id));
        
        // Update existing events and add new ones
        const updatedEvents = [...state.events];
        
        // Update existing events with any new data
        newEvents.forEach(newEvent => {
          const existingIndex = updatedEvents.findIndex(e => e.id === newEvent.id);
          if (existingIndex !== -1) {
            // Merge new data into existing event
            updatedEvents[existingIndex] = { ...updatedEvents[existingIndex], ...newEvent };
          }
        });
        
        // Add truly new events
        const finalEvents = [...updatedEvents, ...trulyNewEvents];
        
        console.log(`✅ Cache updated: ${trulyNewEvents.length} new, ${newEvents.length - trulyNewEvents.length} updated, ${finalEvents.length} total`);
        
        return {
          events: finalEvents,
          cache: {
            ...state.cache,
            events: finalEvents,
            lastFetch: new Date()
          }
        };
      });
    },
    
    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================
    
    _setEventsLoaded: (data) => {
      console.log('📡 EventsStore: Events received:', data.mode);
      
      const state = get();
      
      if (data.mode === 'calendar') {
        // Calendar mode: accumulate calendar events and add to main cache
        const newFormattedEvents = formatEventsForCalendar(data.events || []);
        
        set(currentState => {
          // Get existing event IDs for deduplication
          const existingIds = new Set(currentState.calendarEvents.map(e => e.id));
          
          // Filter out events that already exist
          const trulyNewEvents = newFormattedEvents.filter(event => !existingIds.has(event.id));
          
          // Update existing events with any new data
          const updatedEvents = [...currentState.calendarEvents];
          newFormattedEvents.forEach(newEvent => {
            const existingIndex = updatedEvents.findIndex(e => e.id === newEvent.id);
            if (existingIndex !== -1) {
              updatedEvents[existingIndex] = { ...updatedEvents[existingIndex], ...newEvent };
            }
          });
          
          // Add truly new events
          const finalCalendarEvents = [...updatedEvents, ...trulyNewEvents];
          
          return {
            calendarEvents: finalCalendarEvents,
            calendarLoading: false,
            cache: {
              ...currentState.cache,
              calendarEvents: finalCalendarEvents,
              lastFetch: new Date()
            }
          };
        });
        
        // Also add to main cache
        state._addEventsToCache(data.events || [], 'calendar');
      } else {
        // List mode: update pagination and add to main cache
        set(currentState => ({
          eventsPagination: data.pagination,
          eventsLoading: false,
          cache: {
            ...currentState.cache,
            pagination: data.pagination
          }
        }));
        
        state._addEventsToCache(data.events || [], 'list');
      }
      
      // Always set eventsLoading to false regardless of mode
      set({ eventsLoading: false });
    },
    
    _setEventDetailsLoaded: (eventData) => {
      console.log('📡 EventsStore: Event details received:', eventData.title);

      set(state => {
        // Add/update in main events array with smart deduplication
        const existingIndex = state.events.findIndex(e => e.id === eventData.id);
        const updatedEvents = existingIndex !== -1
          ? state.events.map((e, index) =>
              index === existingIndex ? { ...e, ...eventData } : e
            )
          : [eventData, ...state.events];

        return {
          events: updatedEvents,
          selectedEvent: eventData,
          selectedEventLoading: false,
          cache: {
            ...state.cache,
            events: updatedEvents,
            selectedEvent: eventData
          }
        };
      });
    },
    
    _addEvent: (eventData) => {
      console.log('📡 EventsStore: New event created:', eventData.title);

      set(state => {
        // Check for duplicates
        const exists = state.events.find(e => e.id === eventData.id);
        if (exists) return state;

        const updatedEvents = [eventData, ...state.events];

        // Format for calendar using the same utility as bulk loading
        // This handles scheduled_datetime -> Date object conversion correctly
        const formattedEvents = formatEventsForCalendar([eventData]);
        const calendarEvent = formattedEvents[0];

        // Only add if formatting succeeded (utility filters invalid dates)
        const updatedCalendarEvents = calendarEvent
          ? [calendarEvent, ...state.calendarEvents]
          : state.calendarEvents;

        return {
          events: updatedEvents,
          calendarEvents: updatedCalendarEvents,
          cache: {
            ...state.cache,
            events: updatedEvents,
            calendarEvents: updatedCalendarEvents
          }
        };
      });
    },
    
    _updateEvent: (eventData) => {
      console.log('📡 EventsStore: Event updated:', eventData.title);

      set(state => {
        // Update in main events array
        const updatedEvents = state.events.map(e =>
          e.id === eventData.id ? { ...e, ...eventData } : e
        );

        // Format updated event for calendar using the same utility
        const formattedEvents = formatEventsForCalendar([eventData]);
        const formattedEvent = formattedEvents[0];

        // Update calendar events
        const updatedCalendarEvents = state.calendarEvents.map(e => {
          if (e.id === eventData.id && formattedEvent) {
            return formattedEvent;
          }
          return e;
        });

        // Update selected event if it's the same one
        const updatedSelectedEvent = state.selectedEvent?.id === eventData.id
          ? { ...state.selectedEvent, ...eventData }
          : state.selectedEvent;

        return {
          events: updatedEvents,
          calendarEvents: updatedCalendarEvents,
          selectedEvent: updatedSelectedEvent,
          cache: {
            ...state.cache,
            events: updatedEvents,
            calendarEvents: updatedCalendarEvents,
            selectedEvent: updatedSelectedEvent
          }
        };
      });
    },
    
    _removeEvent: (eventId) => {
      console.log('📡 EventsStore: Event deleted:', eventId);

      set(state => {
        // Remove from all caches
        const updatedEvents = state.events.filter(e => e.id !== eventId);
        const updatedCalendarEvents = state.calendarEvents.filter(e => e.id !== eventId);

        // Clear selected event if it was deleted
        const updatedSelectedEvent = state.selectedEvent?.id === eventId
          ? null
          : state.selectedEvent;

        return {
          events: updatedEvents,
          calendarEvents: updatedCalendarEvents,
          selectedEvent: updatedSelectedEvent,
          cache: {
            ...state.cache,
            events: updatedEvents,
            calendarEvents: updatedCalendarEvents,
            selectedEvent: updatedSelectedEvent
          }
        };
      });
    },

    // ================================
    // UTILITY METHODS (from EventsSocketService)
    // ================================
    
    _formatEventForCalendar: (eventData) => {
      return {
        id: eventData.id,
        title: eventData.title || `${eventData.service_type} - ${eventData.customer_name}`,
        start: `${eventData.event_date}T${eventData.event_time}`,
        duration: eventData.duration || 60,
        backgroundColor: get()._getEventColor(eventData.event_category, eventData.status),
        borderColor: get()._getEventBorderColor(eventData.status),
        extendedProps: {
          category: eventData.event_category,
          status: eventData.status,
          customerName: eventData.customer_name,
          serviceType: eventData.service_type,
          description: eventData.description
        }
      };
    },
    
    _getEventColor: (category, status) => {
      if (status === 'completed') return '#22c55e';
      if (status === 'cancelled') return '#ef4444';
      if (category === 'service') return '#3b82f6';
      if (category === 'general') return '#8b5cf6';
      return '#6b7280';
    },
    
    _getEventBorderColor: (status) => {
      if (status === 'confirmed') return '#059669';
      if (status === 'pending') return '#d97706';
      return 'transparent';
    },
    
    // ================================
    // INTERNAL METHODS (socket manager, initialization)
    // ================================
    
    _getSocketManager: () => {
      // Access socket manager from window global
      return { 
        socketManager: typeof window !== 'undefined' ? window.__socketManager : null 
      };
    },
    
  _initialize: (socketManager) => {
    console.log('🔌 EventsStore: Initializing with socket manager');
    
    if (typeof window !== 'undefined') {
      window.__socketManager = socketManager;
    }
    
    set({ 
      isConnected: true,
      _initialized: true 
    });
    
    // Auto-load events on initialization (wider range like provider)
    // Remove the check for empty events - always reload on initialization
    console.log('🔄 EventsStore: Auto-loading events (first time)');
    
    const today = new Date();
    const pastDate = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);  // 30 days ago
    const futureDate = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000); // 90 days ahead
    
    get().loadEvents({
      start_date: pastDate.toISOString().split('T')[0],
      end_date: futureDate.toISOString().split('T')[0]
    });
  },
    
    _setConnectionState: (connected) => {
      set({ isConnected: connected });
    },
    
    _handleAuthReset: () => {
      console.log('🧹 EventsStore: Resetting all state due to auth reset');

      set({
        // Reset all state to initial values
        events: [],
        eventsLoading: false,
        eventsPagination: null,
        selectedEvent: null,
        selectedEventLoading: false,
        calendarEvents: [],
        calendarLoading: false,
        isConnected: false,
        cache: {
          events: [],
          pagination: null,
          selectedEvent: null,
          calendarEvents: [],
          lastFetch: null
        },
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENERS SETUP (Global initialization)
// ================================


export const initializeEventsStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('👂 EventsStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  // Set up universal response listener for all event events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useEventsStore.getState();
    
    switch (eventName) {
      // ✅ SUCCESS EVENTS
      case SOCKET_EVENTS.EVENTS_LOADED:
        store._setEventsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.EVENT_DETAILS_LOADED:
        store._setEventDetailsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.EVENT_CREATED:
        // Handle different possible response structures
        const eventData = responseData.data?.event || responseData.data || responseData;
        store._addEvent(eventData);
        break;
        
      case SOCKET_EVENTS.EVENT_UPDATED:
      case SOCKET_EVENTS.EVENT_STATUS_UPDATED:
        store._updateEvent(responseData.data);
        break;
        
      case SOCKET_EVENTS.EVENT_DELETED:
        store._removeEvent(responseData.data.event_id);
        break;
        
      // Partner service events (cross-domain functionality)
      case SOCKET_EVENTS.PARTNER_SERVICE_CREATED:
        console.log('📡 EventsStore: Partner service created');
        // Don't add events to cache here - they come via separate broadcasts
        break;

      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_EVENTS_FAILED:
        set({ eventsLoading: false, calendarLoading: false });
        break;

      case SOCKET_EVENTS.GET_EVENT_DETAILS_FAILED:
        set({ selectedEventLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_EVENT_FAILED:
      case SOCKET_EVENTS.UPDATE_EVENT_FAILED:
      case SOCKET_EVENTS.DELETE_EVENT_FAILED:
      case SOCKET_EVENTS.UPDATE_EVENT_STATUS_FAILED:
      case SOCKET_EVENTS.CREATE_PARTNER_SERVICE_FAILED:
        // Just log - NotificationListener shows error to user
        console.log('❌ EventsStore: Operation failed -', responseData.message);
        break;
        
      default:
        // Not an events-related event, ignore
        break;
    }
  };
  
  // Set up individual event listeners using existing system
  const eventEvents = [
    // Success events
    SOCKET_EVENTS.EVENTS_LOADED,
    SOCKET_EVENTS.EVENT_DETAILS_LOADED,
    SOCKET_EVENTS.EVENT_CREATED,
    SOCKET_EVENTS.EVENT_UPDATED,
    SOCKET_EVENTS.EVENT_DELETED,
    SOCKET_EVENTS.EVENT_STATUS_UPDATED,
    SOCKET_EVENTS.PARTNER_SERVICE_CREATED,
    // Error events
    SOCKET_EVENTS.GET_EVENTS_FAILED,
    SOCKET_EVENTS.GET_EVENT_DETAILS_FAILED,
    SOCKET_EVENTS.CREATE_EVENT_FAILED,
    SOCKET_EVENTS.UPDATE_EVENT_FAILED,
    SOCKET_EVENTS.DELETE_EVENT_FAILED,
    SOCKET_EVENTS.UPDATE_EVENT_STATUS_FAILED,
    SOCKET_EVENTS.CREATE_PARTNER_SERVICE_FAILED
  ];
  
  // Set up individual event listeners using existing system
  eventEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  // Set up auth reset listener
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useEventsStore.getState()._handleAuthReset();
    });
  }
  
  // Initialize the store
  useEventsStore.getState()._initialize(socketManager);
  
  console.log('✅ EventsStore: Socket event listeners initialized');
};

export default useEventsStore;